// Step 3: look at anything flagged, fix headings, then save renders and model to the project.
import * as THREE from 'three';
import { PLAN } from '@archviewvr/config';
import { levelName, type ProjectManifest } from '@archviewvr/engine';
import { checkoutUrl, checkAdmin, adminGiveFree, manifestBrand, getHotspots, hotspotToManifest, getProject, getPanoramas, getAlternates, getLevels, clearProjectContent, deleteFile, uploadFile, insert, update, syncPublished, type Project } from '../data';
import { getWork, newWork, processFiles, dropWork, storageOf, applyScale, fitScale, scaleFit, bakeModel, snapshotScale, restoreScale, MODEL_UNITS, type Work, type WorkPano } from '../process';
import { attachOrbit, baseOf } from './upload';
import { html, raw, icon, $, $$, flowHeader, formatBytes, formatLimit, toast, confirmDialog, choiceDialog } from '../ui';
import { TRIAL_ALTS } from './alts-panel';

const D = 180 / Math.PI;
const norm = (deg: number) => ((deg % 360) + 540) % 360 - 180;

export async function checkView(root: HTMLElement, projectId: string): Promise<void> {
  const project = await getProject(projectId);
  if (!project) { location.hash = '#/'; return; }
  let w = getWork(projectId);
  if (w?.running) { location.hash = `#/p/${projectId}/upload`; return; }
  if (!w || !w.done) {
    // nothing dropped: open the saved tour so its scale and headings can be adjusted
    const saved = await getPanoramas(projectId);
    if (!saved.length) { location.hash = `#/p/${projectId}/upload`; return; }
    root.innerHTML = html`<div class="loading-page">${icon('spin', 18)} Opening the saved renders and model…</div>`.html;
    w = newWork(projectId);
    w.base = baseOf(project, saved, (await getLevels(projectId)).map((l) => +l.height));
    w.fromSaved = true;
    w.scaleCams = true;
    await processFiles(w, []);
    if (!w.done) { toast('Couldn’t open the saved tour. Try again, or drop the files again.', 'error', 7000); location.hash = `#/p/${projectId}`; return; }
  }
  render(root, project, w);
}

function render(root: HTMLElement, project: Project, w: Work): void {
  const usable = () => w.panos.filter((p) => p.ep);
  const flagged = () => w.panos.filter((p) => p.status === 'warn' || p.status === 'bad');
  let tab: 'flagged' | 'all' = flagged().length ? 'flagged' : 'all';
  let sel: WorkPano = flagged()[0] ?? usable()[0];

  root.innerHTML = html`<div class="flow">
    ${flowHeader({ back: w.fromSaved ? `#/p/${project.id}` : `#/p/${project.id}/upload`, eyebrow: w.fromSaved ? 'Adjust renders' : 'New project', title: project.name, step: project.state === 'published' ? undefined : 3, project: project.id, canCheck: true })}
    <div class="ck-top"><div class="box summary" id="summary"></div><div class="box fix" id="fix"></div></div>
    <div class="flow-body ck-grid">
      <aside class="ck-side">
        <section class="check-list" aria-label="Renders"><div class="tabs" id="tabs"></div><div id="rows"></div></section>
        <div class="box scale-box" id="scale-box"></div>
      </aside>
      <section class="ck-main">
        <div class="stage" id="stage">
          <div class="tl"><span class="mono" id="sel-name"></span><label for="edges"><input id="edges" type="checkbox" ${w.depthBuilt ? '' : 'checked'}> Model edges</label></div>
        </div>
      </section>
    </div>
  </div>`.html;

  // engine on the stage, standing at the selected render with model edges on
  const e = w.engine;
  const stage = $('#stage', root);
  stage.prepend(w.canvas);
  // a model made from the depth maps lines up by itself, and its edges are a scribble: off to start
  e.settings.edges = !w.depthBuilt;
  e.settings.settle = false;
  e.resetMode('walk');
  const size = () => { const r = stage.getBoundingClientRect(); if (r.width) e.resize(r.width, r.height); };
  const ro = new ResizeObserver(size); ro.observe(stage); size();
  e.start();
  attachOrbit(w, w.canvas);
  $<HTMLInputElement>('#edges', root).onchange = (ev) => { e.settings.edges = (ev.target as HTMLInputElement).checked; e.applyOverlays(); };

  const drawSummary = () => {
    const u = usable(), f = flagged();
    const keptN = w.panos.filter((p) => p.kept).length;
    const alts = w.panos.filter((p) => p.altOf);
    const altNames = [...new Set(alts.map((p) => p.scheme!))].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })).join(', ');
    const lined = u.filter((p) => p.status === 'ok').length;
    const trialCap = project.state === 'trial' && u.length > PLAN.trialPanoLimit;
    $('#summary', root).innerHTML = html`
      <div class="l">
        <span class="big-badge ${f.length ? 'badge warn' : 'badge ok'}" style="flex:0 0 40px;height:40px">${f.length ? '!' : icon('tick', 18)}</span>
        <div><div style="font-weight:700;font-size:16px">${lined} of ${u.length} renders line up with the model</div>
        <div class="muted" style="font-size:13px">${f.length ? `${f.length} need${f.length === 1 ? 's' : ''} a quick look.` : 'Everything looks right.'}${trialCap ? ` A trial keeps ${PLAN.trialPanoLimit}, so you’ll choose a plan when you save.` : ''}</div>
        ${alts.length ? html`<div class="muted" style="font-size:13px">${alts.length} share${alts.length === 1 ? 's' : ''} a camera with another render, so ${alts.length === 1 ? 'it’s' : 'they’re'} saved as alternate finishes (${altNames}) of the ${w.mainScheme ?? 'main'} renders.${project.state === 'trial' && alts.length > TRIAL_ALTS ? ` As a trial, the first ${TRIAL_ALTS} are saved.` : ''}</div>` : ''}</div>
      </div>
      <div class="accept-col">
        <button class="btn primary" id="accept">Accept and save</button>
        ${keptN && !w.fromSaved ? html`<label class="toggle small-t"><input type="checkbox" id="drop-kept"><span>Remove the ${keptN} saved render${keptN === 1 ? '' : 's'} not in this drop</span></label>` : ''}
      </div>`.html;
    $('#accept', root).onclick = () => accept(root, project, w, !!$<HTMLInputElement>('#drop-kept', root)?.checked);
  };

  const drawRows = () => {
    const list = tab === 'flagged' ? flagged() : w.panos;
    $('#tabs', root).innerHTML = html`<button class="${tab === 'flagged' ? 'on' : ''}" data-tab="flagged">Needs a look · ${flagged().length}</button><button class="${tab === 'all' ? 'on' : ''}" data-tab="all">All · ${w.panos.length}</button>`.html;
    $$('[data-tab]', root).forEach((b) => (b.onclick = () => { tab = b.dataset.tab as typeof tab; drawRows(); }));
    $('#rows', root).innerHTML = list.length ? list.map((p) => {
      const pill = p.altOf ? '<span class="pill kept">Alternate</span>' : p.kept ? `<span class="pill kept">${w.fromSaved ? 'Saved' : 'Kept'}</span>` : p.replaces && p.status === 'ok' ? '<span class="pill ok">Replaced</span>' : p.status === 'ok' ? '<span class="pill ok">Lined up</span>' : p.status === 'warn' ? '<span class="pill warn">Check</span>' : '<span class="pill bad">Not used</span>';
      const mainOf = p.altOf ? w.panos.find((x) => x.id === p.altOf) : null;
      const lv = mainOf ? `${p.scheme} finish, same camera as the ${mainOf.scheme ?? 'main'} render` : p.level != null ? levelName(p.level) : p.ep ? 'Between levels' : 'Not placed';
      return html`<button class="rrow ${p === sel ? 'on' : ''}" data-id="${p.id}"><img src="${p.thumbUrl}" alt=""><span class="grow"><span class="nm">${p.name}</span><span class="lv">${lv}</span></span>${raw(pill)}</button>`.html;
    }).join('') : '<p class="muted" style="padding:10px">Nothing to check.</p>';
    $$('.rrow', root).forEach((b) => (b.onclick = () => { sel = w.panos.find((p) => p.id === b.dataset.id)!; select(); }));
  };

  const drawFix = () => {
    const p = sel;
    const fix = $('#fix', root);
    $('#sel-name', root).textContent = p.name;
    const removeBtn = html`<button class="btn small ghost-bad" id="rm-pano">${icon('trash', 14)}${p.kept ? 'Remove from the tour' : 'Leave out'}</button>`;
    const wireRemove = () => $('#rm-pano', root)?.addEventListener('click', () => void removePano(p));
    if (p.altOf) {
      const main = w.panos.find((x) => x.id === p.altOf);
      fix.innerHTML = html`<div class="why"><span class="badge ok">${icon('tick', 14)}</span><div><div style="font-weight:700">Saved as the ${p.scheme} finish of ${main?.name ?? 'its main render'}</div>
        <div class="muted" style="font-size:13px;line-height:1.5;margin-top:4px">It uses the same camera, so buyers switch to it with a button rather than walking to it. Rename or remove finishes later in Set up tour → Alternate panoramas.</div></div></div>
        <div class="btn-row">${main?.ep ? html`<button class="btn small" id="go-main">Show ${main.name}</button>` : ''}${removeBtn}</div>`.html;
      $('#go-main', root)?.addEventListener('click', () => { sel = main!; select(); });
      wireRemove();
      return;
    }
    if (!p.ep) {
      fix.innerHTML = html`<div class="why"><span class="badge bad">×</span><div><div style="font-weight:700">${p.name} isn’t used</div>
        <div class="muted" style="font-size:13px;line-height:1.5;margin-top:4px">${p.note === 'unreadable' ? 'The image couldn’t be read.' : `There’s no camera called ${p.name} in the points file. Rename the render to match its camera, or re-export the points, then drop the folder again.`}</div></div></div>
        <div class="btn-row">${removeBtn}</div>`.html;
      wireRemove();
      return;
    }
    if (p.note === 'same camera') {
      const others = w.panos.filter((x) => x !== p && x.cam && x.cam === p.cam).length;
      fix.innerHTML = html`<div class="why"><span class="badge warn">!</span><div><div style="font-weight:700">${p.name} matched camera ${p.cam}, like ${others} other render${others === 1 ? '' : 's'}</div>
        <div class="muted" style="font-size:13px;line-height:1.5;margin-top:4px">Their numbers differ, so they’re probably different views whose names don’t match their cameras. They all sit at the same spot. Rename the renders (or the cameras in 3ds Max) so each one matches its own camera, then drop the folder again.</div></div></div>
        <div class="btn-row">${removeBtn}</div>`.html;
      wireRemove();
      return;
    }
    const deg = norm(p.ep.yaw * D);
    const shared = p.headingShared != null ? norm(p.headingShared) : null;
    const own = p.headingOwn != null ? norm(p.headingOwn) : null;
    fix.innerHTML = html`
      ${p.status === 'warn' ? html`<div class="why"><span class="badge warn">!</span><div><div style="font-weight:700">This render matched best at ${own?.toFixed(1)}°, but the other cameras share ${shared?.toFixed(1)}°</div>
        <div class="muted" style="font-size:13px;line-height:1.5;margin-top:4px">Check the model edges against door frames and wall corners. Use whichever lines up.</div></div></div>` : ''}
      <div class="range"><div class="top"><label for="heading">Heading</label><output id="h-out" class="mono">${deg.toFixed(1)}°</output></div>
        <input id="heading" type="range" min="-180" max="180" step="0.1" value="${deg.toFixed(1)}"></div>
      <div class="btn-row">
        ${shared != null ? html`<button class="btn ${p.status === 'warn' ? 'primary' : ''} small" id="use-shared">Use shared ${shared.toFixed(1)}°</button>` : ''}
        ${own != null && p.status === 'warn' ? html`<button class="btn small" id="keep-own">Keep ${own.toFixed(1)}°</button>` : ''}
        ${p.status === 'warn' ? html`<button class="btn small" id="looks-right">Looks right</button>` : ''}
        <span class="spacer"></span>${removeBtn}
      </div>`.html;
    wireRemove();
    const setYaw = (d: number) => {
      p.ep!.yaw = d / D;
      e.heading = p.ep!.yaw;
      $('#h-out', root).textContent = `${norm(d).toFixed(1)}°`;
      $<HTMLInputElement>('#heading', root).value = norm(d).toFixed(1);
    };
    // a decision on a flagged render resolves it and moves on to the next one
    const resolve = (d: number) => {
      setYaw(d);
      if (p.status === 'warn') { p.status = 'ok'; p.note = ''; }
      const next = flagged()[0];
      if (next) { sel = next; select(); } else { drawRows(); drawFix(); }
      drawSummary();
    };
    $<HTMLInputElement>('#heading', root).oninput = (ev) => setYaw(Number((ev.target as HTMLInputElement).value));
    $<HTMLInputElement>('#heading', root).onchange = () => { if (p.status === 'warn') { p.status = 'ok'; p.note = ''; drawRows(); drawSummary(); } };
    $('#use-shared', root)?.addEventListener('click', () => resolve(shared!));
    $('#keep-own', root)?.addEventListener('click', () => resolve(own!));
    $('#looks-right', root)?.addEventListener('click', () => resolve(deg));
  };

  const select = () => {
    if (sel.ep) e.jumpTo(sel.ep, sel.ep.yaw, 0);
    drawRows(); drawFix();
  };

  // leave a render out of this upload (or take a saved one out of the tour); its finishes go with it
  const removePano = async (p: WorkPano) => {
    const finishes = w.panos.filter((x) => x.altOf === p.id);
    if (p.kept || finishes.length) {
      const ok = await confirmDialog({
        title: `Remove ${p.name}?`,
        body: `${p.kept ? 'It’s already saved, so it comes out of the tour when you save. ' : ''}${finishes.length ? `Its ${finishes.length} alternate finish${finishes.length === 1 ? '' : 'es'} go${finishes.length === 1 ? 'es' : ''} too.` : ''}`,
        confirm: 'Remove', danger: true,
      });
      if (!ok) return;
    }
    for (const x of [p, ...finishes]) {
      if (x.ep) { const i = e.panos.indexOf(x.ep); if (i >= 0) e.panos.splice(i, 1); x.ep.baseTex.dispose(); x.ep = null; }
      if (x.thumbUrl.startsWith('blob:')) URL.revokeObjectURL(x.thumbUrl);
      w.panos.splice(w.panos.indexOf(x), 1);
    }
    e.refresh({ depth: false });
    const next = flagged()[0] ?? usable()[0] ?? w.panos[0];
    if (!next) { toast('That was the last render. Drop your renders again to carry on.'); location.hash = `#/p/${project.id}/upload`; return; }
    sel = next;
    drawSummary(); select();
    toast(`${p.name} left out`);
  };

  // model scale: the units the model file was in, a fine-tune, and a fit to the cameras
  const original = snapshotScale(w);
  const nearestUnit = (s: number) => MODEL_UNITS.reduce((a, b) => (Math.abs(Math.log(s / b[0])) < Math.abs(Math.log(s / a[0])) ? b : a));
  // the units stay as chosen; the fine-tune is whatever's left over
  // overall scale = file units → metres; the engine's scale is that over w.fileUnit
  const overall = () => e.modelScale * w.fileUnit;
  let unit = nearestUnit(overall())[0];
  const drawScale = () => {
    const s = overall();
    const u = unit;
    const fine = (s / u) * 100;
    const sz = e.bbox.getSize(new THREE.Vector3());
    const eye = scaleFit(w, s).eye;
    const eyeOk = eye != null && eye > 0.9 && eye < 2.3;
    $('#scale-box', root).innerHTML = html`
      <div class="sc-head"><div style="font-weight:700">Model scale</div>
        <div class="btn-row">${w.rescaled ? html`<button class="btn small" id="sc-undo">Undo</button>` : ''}<button class="btn small" id="sc-fit" title="Picks the units that put the cameras at eye height">Fit to cameras</button></div></div>
      <div class="sc-row">
        <label class="sc-f"><span>Model units</span><select id="sc-unit">${raw(MODEL_UNITS.map(([v, n]) => `<option value="${v}" ${v === u ? 'selected' : ''}>${n}</option>`).join(''))}</select></label>
        <label class="sc-f"><span>Fine-tune</span><span class="sc-pct"><input id="sc-fine" type="number" min="1" max="1000" step="any" value="${+fine.toFixed(3)}"><i>%</i></span></label>
      </div>
      <label class="toggle small-t"><input type="checkbox" id="sc-cams" ${w.scaleCams ? 'checked' : ''}><span>Cameras move with the model</span></label>
      <div class="muted sc-note">${sz.x.toFixed(1)} × ${sz.z.toFixed(1)} m${eye != null ? ` · cameras stand ${eye.toFixed(2)} m above the floor` : ' · the cameras aren’t over the model, try other units'}${eye != null && !eyeOk ? ' · that looks off, try other units' : ''}</div>`.html;
    const setScale = async (next: number) => {
      if (!(next > 0) || Math.abs(next - e.modelScale) < 1e-12) return;
      const box = $('#scale-box', root);
      box.classList.add('busy');
      await new Promise((r) => requestAnimationFrame(() => setTimeout(r, 30)));
      applyScale(w, next);
      box.classList.remove('busy');
      drawScale(); drawSummary(); select();
    };
    // new units start from 100%
    $<HTMLSelectElement>('#sc-unit', root).onchange = (ev) => { unit = Number((ev.target as HTMLSelectElement).value); void setScale(unit / w.fileUnit).then(drawScale); };
    $<HTMLInputElement>('#sc-fine', root).onchange = (ev) => {
      const pct = Math.min(1000, Math.max(1, Number((ev.target as HTMLInputElement).value) || 100));
      void setScale((unit * pct) / 100 / w.fileUnit).then(drawScale);
    };
    $('#sc-undo', root)?.addEventListener('click', () => { restoreScale(w, original); unit = nearestUnit(overall())[0]; drawScale(); drawSummary(); select(); });
    $<HTMLInputElement>('#sc-cams', root).onchange = (ev) => { w.scaleCams = (ev.target as HTMLInputElement).checked; drawScale(); };
    $('#sc-fit', root).onclick = () => {
      const fit = fitScale(w);
      if (!fit) { toast('The current scale already fits the cameras best.'); return; }
      unit = nearestUnit(fit.scale * w.fileUnit)[0];
      void setScale(fit.scale).then(() => { drawScale(); toast(fit.name.startsWith('×') ? `Model scaled ${fit.name}` : `Model read in ${fit.name.toLowerCase()}`); });
    };
  };
  drawSummary();
  if (w.depthBuilt) {
    // made from the depth maps, in metres: nothing to scale
    $('#scale-box', root).innerHTML = html`<div class="sc-head"><div style="font-weight:700">Model</div></div>
      <div class="muted sc-note">Made from your depth maps, in metres. It only has what the renders saw, so the 3D plan, clicks and measuring are less exact than with a model file. Drop a model with the renders to use that instead.</div>`.html;
  } else drawScale();
  select();
}

/**
 * Accept and save. A trial keeps its first renders only, so with more than that the studio picks first:
 * save those as a trial, or publish and keep them all (admins can also give the project a free month).
 */
async function accept(root: HTMLElement, project: Project, w: Work, removeKept: boolean): Promise<void> {
  const names = new Set(w.panos.filter((p) => p.ep && !(removeKept && p.kept)).map((p) => p.name));
  const n = names.size, cap = PLAN.trialPanoLimit;
  if (project.state !== 'trial' || n <= cap) { await save(root, project, w, removeKept); return; }
  const admin = await checkAdmin();
  const pick = await choiceDialog<'pay' | 'trial' | 'free'>({
    title: `${n} renders, trials keep ${cap}`,
    body: html`<p>A trial tour keeps its first ${cap} renders and shows a watermark. Publish to keep all ${n}.</p>`,
    choices: [
      { value: 'pay', label: `Publish and save all ${n}`, note: `$${PLAN.priceAud} AUD a month for this tour. Pay in a new tab, then the rest upload.`, primary: true },
      { value: 'trial', label: `Save the first ${cap} as a trial`, note: 'You can publish later and upload the rest again.' },
      ...(admin ? [{ value: 'free' as const, label: `Give it a free month and save all ${n}`, note: 'Admins only. The same as Admin → Free month.' }] : []),
    ],
  });
  if (!pick) return;
  if (pick === 'trial') { await save(root, project, w, removeKept); return; }
  if (pick === 'free') {
    try { await adminGiveFree(project.id, 1); }
    catch (e) { toast(`Couldn’t give a free month: ${e instanceof Error ? e.message : 'unknown error'}`, 'error', 8000); return; }
    const fresh = await getProject(project.id);
    if (!fresh) return;
    toast('Free month given. Saving all renders.');
    await save(root, fresh, w, removeKept);
    return;
  }
  // pay: Stripe needs a saved tour, so the first renders are saved as a trial, then the rest once it's paid for
  const tab = window.open('', '_blank');
  tab?.document.write('<p style="font:15px system-ui;margin:40px">Opening the payment page…</p>');
  const first = await save(root, project, w, removeKept, true);
  if (!first) { tab?.close(); return; }
  let url: string;
  try { url = await checkoutUrl(project.id); }
  catch (e) {
    tab?.close();
    toast(`The first ${cap} are saved, but the payment page didn’t open: ${e instanceof Error ? e.message : 'unknown error'}. Publish from the project page, then upload the rest.`, 'error', 10000);
    dropWork(project.id); location.hash = `#/p/${project.id}`; return;
  }
  if (tab && !tab.closed) tab.location.href = url;
  // the renders just saved stay as they are next time; their files are already uploaded
  const savedAlts = new Set(first.alts);
  for (const p of first.keep) { p.kept = true; p.replaces = false; p.depthPng = null; }
  w.panos = w.panos.filter((p) => !savedAlts.has(p));
  w.modelBlob = null;

  const overlay = document.createElement('div');
  overlay.className = 'overlay';
  overlay.innerHTML = html`<div class="panel"><h2>Finish paying to publish</h2>
    <div class="muted">The first ${cap} renders are saved. Pay in the Stripe tab and the other ${n - cap} upload here straight after. Keep this page open.</div>
    <div class="btn-row"><a class="btn primary" href="${url}" target="_blank" rel="noopener">Open the payment page</a><button class="btn" id="pay-stop">Keep it as a trial</button></div></div>`.html;
  root.appendChild(overlay);
  let stopped = false;
  $('#pay-stop', overlay).onclick = () => { stopped = true; overlay.remove(); dropWork(project.id); location.hash = `#/p/${project.id}`; };
  while (!stopped) {
    await new Promise((r) => setTimeout(r, 3000));
    if (stopped) return;
    const fresh = await getProject(project.id).catch(() => null);
    if (fresh?.state === 'published') {
      overlay.remove();
      toast('Published. Saving the rest of the renders.');
      await save(root, fresh, w, false);
      return;
    }
  }
}

/** Uploads what's new and records the project in the database. Saved renders that weren't in the drop
 * stay as they are (unless the studio chose to remove them); re-renders keep their id. */
async function save(root: HTMLElement, project: Project, w: Work, removeKept = false, stay = false): Promise<{ keep: WorkPano[]; alts: WorkPano[] } | null> {
  // a model whose scale changed here is written again, in metres
  if (w.rescaled) { w.modelBlob = await bakeModel(w); w.rescaled = false; }
  // one render per name (the database allows no two the same), checked before anything saved is touched
  const names = new Set<string>();
  const all = w.panos.filter((p) => p.ep && !(removeKept && p.kept)).filter((p) => !names.has(p.name) && !!names.add(p.name));
  const keep = project.state === 'trial' ? all.slice(0, PLAN.trialPanoLimit) : all;
  const before = await getPanoramas(project.id);
  const beforeById = new Map(before.map((p) => [p.id, p]));
  // alternate panoramas (Set up tour) stay with their main render, matched by name
  const altRows = await getAlternates(project.id).catch(() => []);
  const keepNames = new Set(keep.map((p) => p.name));
  // renders in this drop that share a camera with a kept render are new alternates (they replace one with the same name)
  const keepIds = new Set(keep.map((p) => p.id));
  let newAlts = w.panos.filter((p) => p.altOf && keepIds.has(p.altOf) && p.base && p.mobile && p.full && p.thumb);
  const nameOfMain = (a: WorkPano) => keep.find((p) => p.id === a.altOf)!.name;
  const replacedAlt = (r: { name: string; set_name: string }) => newAlts.some((a) => nameOfMain(a) === r.name && a.scheme === r.set_name);
  let altsKept = altRows.filter((a) => keepNames.has(a.name));
  if (project.state === 'trial') newAlts = newAlts.slice(0, Math.max(0, TRIAL_ALTS - altsKept.filter((r) => !replacedAlt(r)).length));
  altsKept = altsKept.filter((r) => !replacedAlt(r));
  const altBytes = altsKept.reduce((n, a) => n + (a.bytes ?? 0), 0) + storageOf(w, newAlts).renders;
  const prevModel = Math.max(0, project.storage_bytes - before.reduce((n, p) => n + (p.bytes ?? 0), 0) - altRows.reduce((n, a) => n + (a.bytes ?? 0), 0));
  const modelBytes = w.modelBlob?.size ?? prevModel;
  const keptBytes = keep.filter((p) => p.kept).reduce((n, p) => n + (beforeById.get(p.id)?.bytes ?? 0), 0);
  const st = storageOf(w, keep.filter((p) => !p.kept));
  const total = st.renders + modelBytes + keptBytes + altBytes;
  if (total > project.storage_limit) { toast(`This is ${formatBytes(total)}, over this project’s ${formatLimit(project.storage_limit)} limit. Remove some renders and try again.`, 'error', 7000); return null; }

  const overlay = document.createElement('div');
  overlay.className = 'overlay';
  overlay.innerHTML = html`<div class="panel"><h2>Saving ${project.name}</h2>
    <div class="muted" id="sv-t">Preparing</div><div class="bar"><i id="sv-p" style="width:0%"></i></div>
    <div class="muted mono" id="sv-b"></div></div>`.html;
  root.appendChild(overlay);
  const say = (t: string) => { $('#sv-t', overlay).textContent = t; };
  let sent = 0;
  const upTotal = (st.total + storageOf(w, newAlts).renders) || 1;
  const done = new Map<string, number>();
  const bump = (key: string, size: number, f: number) => {
    sent += size * f - (done.get(key) ?? 0);
    done.set(key, size * f);
    $('#sv-p', overlay).style.width = `${Math.min(100, (sent / upTotal) * 100).toFixed(1)}%`;
    $('#sv-b', overlay).textContent = `${formatBytes(sent)} of ${formatBytes(upTotal)}`;
  };
  try {
    say('Preparing the project');
    // keep what the studio set up last time: rooms (by id, then by render name), the start view, tour options
    const roomByName = new Map(before.filter((p) => p.room).map((p) => [p.name, p.room!]));
    const roomOf = (p: WorkPano) => beforeById.get(p.id)?.room ?? roomByName.get(p.name) ?? null;
    const oldStart = before.find((p) => p.id === project.start_pano_id);
    const startPano = (oldStart && keep.find((p) => p.id === oldStart.id || p.name === oldStart.name)) || keep[0];
    const startView = oldStart && startPano.name === oldStart.name
      ? { heading: project.start_heading ?? 0, pitch: project.start_pitch ?? 0 }
      : { heading: +norm(keep[0].ep!.yaw * D).toFixed(2), pitch: 0 };
    // the original model units, so a later check can offer them again
    const settings: Record<string, unknown> = { ...(project.settings ?? {}), flip: w.engine.settings.flip, ...(w.modelRoot ? { modelUnit: +(w.engine.modelScale * w.fileUnit).toPrecision(6) } : {}) };
    // a model made from depth maps is made again when they're dropped again; a model file ends that
    if (w.depthBuilt) settings.depthModel = true;
    else if (w.modelBlob && !w.fromSaved) delete settings.depthModel;
    // new finishes: their names join the button order, and the main renders take the scheme name they share
    if (newAlts.length) {
      const old = (settings.schemes as string[] | undefined) ?? [];
      const names = [...new Set(newAlts.map((a) => a.scheme!))].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
      settings.schemes = [...old, ...names.filter((n) => !old.includes(n))];
      if (!settings.schemeDefault && w.mainScheme) settings.schemeDefault = w.mainScheme;
    }
    const oldLevels = await getLevels(project.id);
    // names and hidden choices carry over from the saved level at about the same height
    const oldHidden = new Set(((project.settings as { hiddenLevels?: string[] } | undefined)?.hiddenLevels) ?? []);
    const used = new Set<string>();
    const carried = w.levels.map((h) => {
      const m = oldLevels.filter((l) => !used.has(l.id) && Math.abs(+l.height - h) < 0.5).sort((a, b) => Math.abs(+a.height - h) - Math.abs(+b.height - h))[0];
      if (m) used.add(m.id);
      return m ?? null;
    });
    const levelNames = w.levels.map((_, i) => carried[i]?.name || levelName(i));
    const levelHidden = w.levels.map((_, i) => !!carried[i] && oldHidden.has(carried[i]!.id));

    // files: new versions get new names, so browsers and the CDN never show an old copy
    const v = Date.now().toString(36);
    const modelKey = w.modelBlob ? (before.length ? `model-${v}.glb` : 'model.glb') : (project.model_key ?? 'model.glb');
    const keys = new Map<string, { base: string; mobile: string; full: string; bytes: number }>();
    // depth maps stay with their render (same camera); not after rescaling a saved tour, as they're in metres
    const rescaled = !!(w.fromSaved && w.modelBlob && Math.abs(w.engine.modelScale - 1) > 1e-9);
    // depth maps in this drop are new files; others carry on from the saved render
    const newDepth = new Map<string, { key: string; bytes: number }>();
    const depthOf = (id: string) => {
      const n = newDepth.get(id); if (n) return n;
      const r = beforeById.get(id); return !rescaled && r?.depth_key ? { key: r.depth_key, bytes: r.depth_bytes ?? 0 } : null;
    };
    const jobs: Array<{ key: string; blob: Blob }> = w.modelBlob ? [{ key: modelKey, blob: w.modelBlob }] : [];
    const altKeys = new Map<string, { base: string; mobile: string; full: string; bytes: number }>();
    for (const a of newAlts) {
      const k = { base: `panos/${a.id}-2k-${v}.jpg`, mobile: `panos/${a.id}-1k-${v}.jpg`, full: `panos/${a.id}-full-${v}.jpg`, bytes: a.base!.size + a.mobile!.size + a.full!.size + a.thumb!.size };
      altKeys.set(a.id, k);
      jobs.push({ key: `thumbs/${a.id}.jpg`, blob: a.thumb! }, { key: k.base, blob: a.base! }, { key: k.mobile, blob: a.mobile! }, { key: k.full, blob: a.full! });
    }
    for (const p of keep) {
      if (p.kept) {
        const r = beforeById.get(p.id)!;
        keys.set(p.id, { base: r.base_key, mobile: r.mobile_key ?? r.base_key, full: r.full_key ?? r.base_key, bytes: r.bytes - (r.depth_bytes ?? 0) });
        continue;
      }
      const sfx = p.replaces ? `-${v}` : '';
      const k = { base: `panos/${p.id}-2k${sfx}.jpg`, mobile: `panos/${p.id}-1k${sfx}.jpg`, full: `panos/${p.id}-full${sfx}.jpg`, bytes: p.base!.size + p.mobile!.size + p.full!.size + p.thumb!.size };
      keys.set(p.id, k);
      jobs.push({ key: `thumbs/${p.id}.jpg`, blob: p.thumb! }, { key: k.base, blob: p.base! }, { key: k.mobile, blob: p.mobile! }, { key: k.full, blob: p.full! });
    }
    for (const p of keep) {
      if (!p.depthPng) continue;
      const k = { key: `depth/${p.id}-${v}.png`, bytes: p.depthPng.size };
      newDepth.set(p.id, k);
      jobs.push({ key: k.key, blob: p.depthPng });
    }
    const added = keep.filter((p) => !p.kept && !p.replaces).length, replaced = keep.filter((p) => p.replaces).length;
    say(before.length
      ? `Uploading ${[added ? `${added} new` : '', replaced ? `${replaced} replaced` : '', w.modelBlob ? 'the new model' : ''].filter(Boolean).join(', ') || 'changes'}`
      : `Uploading ${keep.length} renders and the model`);
    let next = 0;
    const worker = async () => {
      while (next < jobs.length) {
        const j = jobs[next++];
        await uploadFile(project.id, j.key, j.blob, (f) => bump(j.key, j.blob.size, f));
      }
    };
    await Promise.all([worker(), worker(), worker()]);

    // database rows: the same ids again, so hotspots, rooms and stats stay attached
    say('Recording renders');
    try {
      // always: a failed earlier save can leave levels behind with no renders
      await clearProjectContent(project.id);
      const levelIds = w.levels.map(() => crypto.randomUUID());
      settings.hiddenLevels = levelIds.filter((_, i) => levelHidden[i]);
      await update('projects', `id=eq.${project.id}`, { storage_bytes: modelBytes, model_key: modelKey, model_format: 'glb', model_scale: 1, model_z_up: false, settings });
      await insert('levels', w.levels.map((h, i) => ({ id: levelIds[i], project_id: project.id, name: levelNames[i], height: h, sort: i })));
      await insert('panoramas', keep.map((p, i) => {
        const k = keys.get(p.id)!;
        const old = beforeById.get(p.id);
        const dm = depthOf(p.id);
        return {
          id: p.id, project_id: project.id, name: p.name, room: roomOf(p), set_name: 'default', level_id: p.level != null ? levelIds[p.level] : null,
          x: p.ep!.pos.x, y: p.ep!.pos.y, z: p.ep!.pos.z, yaw: norm(p.ep!.yaw * D),
          check_state: p.kept ? old?.check_state ?? 'ok' : p.status === 'ok' ? 'ok' : 'check',
          base_key: k.base, mobile_key: k.mobile, full_key: k.full, bytes: k.bytes + (dm?.bytes ?? 0), sort: i,
          ...(dm ? { depth_key: dm.key, depth_bytes: dm.bytes } : {}),
        };
      }));
      const parent = new Map(keep.map((p) => [p.name, p]));
      if (altsKept.length) {
        await insert('panoramas', altsKept.map((a) => {
          const p = parent.get(a.name)!;
          return { ...a, x: p.ep!.pos.x, y: p.ep!.pos.y, z: p.ep!.pos.z, yaw: norm(p.ep!.yaw * D), level_id: p.level != null ? levelIds[p.level] : null, sort: keep.indexOf(p) };
        }));
      }
      if (newAlts.length) {
        await insert('panoramas', newAlts.map((a) => {
          const p = keep.find((x) => x.id === a.altOf)!, k = altKeys.get(a.id)!;
          return {
            id: a.id, project_id: project.id, name: p.name, room: null, set_name: a.scheme, level_id: p.level != null ? levelIds[p.level] : null,
            x: p.ep!.pos.x, y: p.ep!.pos.y, z: p.ep!.pos.z, yaw: norm(p.ep!.yaw * D), check_state: 'ok',
            base_key: k.base, mobile_key: k.mobile, full_key: k.full, bytes: k.bytes, sort: keep.indexOf(p),
          };
        }));
      }
      await update('projects', `id=eq.${project.id}`, { start_pano_id: startPano.id, start_heading: startView.heading, start_pitch: startView.pitch });
    } catch (err) {
      // put the saved renders and levels back, so a failed save never leaves the project empty
      if (before.length) {
        try {
          await clearProjectContent(project.id);
          if (oldLevels.length) await insert('levels', oldLevels);
          await insert('panoramas', [...before, ...altRows]);
          await update('projects', `id=eq.${project.id}`, { storage_bytes: project.storage_bytes, model_key: project.model_key, settings: project.settings ?? {}, start_pano_id: project.start_pano_id, start_heading: project.start_heading ?? 0, start_pitch: project.start_pitch ?? 0 });
        } catch { /* the original error matters more */ }
      }
      throw err;
    }

    // tidy up files nothing points at any more (old versions and removed renders)
    const live = new Set<string>([modelKey]);
    for (const k of keys.values()) { live.add(k.base); live.add(k.mobile); live.add(k.full); }
    for (const p of keep) { const dm = depthOf(p.id); if (dm) live.add(dm.key); }
    for (const a of altsKept) for (const k of [a.base_key, a.mobile_key, a.full_key]) if (k) live.add(k);
    for (const k of altKeys.values()) { live.add(k.base); live.add(k.mobile); live.add(k.full); }
    const altGone = altRows.filter((a) => !altsKept.includes(a));
    const stale: string[] = [];
    if (project.model_key && !live.has(project.model_key)) stale.push(project.model_key);
    for (const r of before) {
      for (const key of [r.base_key, r.mobile_key, r.full_key, r.depth_key]) if (key && !live.has(key)) stale.push(key);
      if (!keys.has(r.id)) stale.push(`thumbs/${r.id}.jpg`);
    }
    for (const a of altGone) { for (const k of [a.base_key, a.mobile_key, a.full_key]) if (k && !live.has(k)) stale.push(k); stale.push(`thumbs/${a.id}.jpg`); }

    // hotspots sit on the model, so they follow a saved tour's model to its new scale
    if (w.fromSaved && w.modelBlob && Math.abs(w.engine.modelScale - 1) > 1e-9) {
      const k = w.engine.modelScale;
      for (const h of await getHotspots(project.id)) await update('hotspots', `id=eq.${h.id}`, { x: h.x * k, y: h.y * k, z: h.z * k });
    }

    // the manifest the tour viewer reads
    say('Writing the tour');
    const manifest: ProjectManifest = {
      version: 1, id: project.id, name: project.name, state: project.state === 'published' ? 'published' : 'trial',
      brand: manifestBrand(project.brand, new Map([...keys].map(([id, k]) => [id, k.base]))), model: { url: modelKey, format: 'glb' },
      levels: w.levels.map((h, i) => ({ name: levelNames[i], height: +h.toFixed(3), ...(levelHidden[i] ? { hidden: true } : {}) })),
      panos: keep.map((p) => ({
        id: p.id, name: p.name, room: roomOf(p) ?? undefined, position: [+p.ep!.pos.x.toFixed(3), +p.ep!.pos.y.toFixed(3), +p.ep!.pos.z.toFixed(3)],
        yaw: +norm(p.ep!.yaw * D).toFixed(2), level: p.level, set: 'default',
        images: { base: keys.get(p.id)!.base, mobile: keys.get(p.id)!.mobile, full: keys.get(p.id)!.full, ...(depthOf(p.id) ? { depth: depthOf(p.id)!.key } : {}) },
        ...(altsKept.some((a) => a.name === p.name) || newAlts.some((a) => a.altOf === p.id) ? { alts: [
          ...altsKept.filter((a) => a.name === p.name).map((a) => ({ id: a.id, set: a.set_name, images: { base: a.base_key, mobile: a.mobile_key ?? a.base_key, full: a.full_key ?? a.base_key } })),
          ...newAlts.filter((a) => a.altOf === p.id).map((a) => ({ id: a.id, set: a.scheme!, images: { base: altKeys.get(a.id)!.base, mobile: altKeys.get(a.id)!.mobile, full: altKeys.get(a.id)!.full } })),
        ] } : {}),
      })),
      hotspots: (await getHotspots(project.id)).map((h) => hotspotToManifest(h)),
      start: { pano: startPano.id, heading: startView.heading, pitch: startView.pitch },
      settings,
      lead: { enabled: !!project.lead_enabled, required: !!project.lead_required },
    };
    await uploadFile(project.id, 'manifest.json', new Blob([JSON.stringify(manifest)], { type: 'application/json' }));
    if (project.state === 'published') { say('Updating the live tour'); await syncPublished(project.id); }
    await Promise.all(stale.map((k) => deleteFile(project.id, k)));
    say('Saved');
    if (stay) { overlay.remove(); return { keep, alts: newAlts }; }
    dropWork(project.id);
    toast(`${project.name} saved`);
    location.hash = `#/p/${project.id}`;
    return { keep, alts: newAlts };
  } catch (e) {
    overlay.remove();
    toast(`Saving stopped: ${e instanceof Error ? e.message : 'unknown error'}. Your work is still here; try again.`, 'error', 9000);
    return null;
  }
}
